import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'
import { isPtTldHostname } from './config/domains'

// Defer non-critical initializations to after load (improves LCP and TBT)
const initNonCritical = () => {
  // Prefetch likely next pages (no bandwidth competition with LCP).
  // Host-aware: .pt gets PT slugs; .com must not prefetch visto-gold (PT-only path).
  const isPtHost = typeof window !== 'undefined' && isPtTldHostname(window.location.hostname);
  const prefetchHrefs = isPtHost
    ? ['/blog', '/contato', '/destinations/portugal/visto-d7', '/destinations/portugal/visto-gold']
    : ['/blog', '/contact', '/destinations/portugal/visa-d7', '/destinations/portugal/golden-visa'];
  prefetchHrefs.forEach((href) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  });

  import('./utils/sentry').then(({ initSentry }) => initSentry());
  import('./utils/googleAnalytics').then(({ initGoogleAnalytics }) => {
    initGoogleAnalytics().then((shouldTrack) => {
      if (!shouldTrack && typeof window.gtag === 'function') {
        (window as any).gtag = () => {};
      }
    });
  });
  import('./utils/analytics').then(({ initCoreWebVitals }) => initCoreWebVitals());
};

// Defer non-critical init to after load + idle to protect LCP and TBT
function scheduleNonCritical() {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(initNonCritical, { timeout: 3000 });
  } else {
    setTimeout(initNonCritical, 2000);
  }
}
if (document.readyState === 'complete') {
  scheduleNonCritical();
} else {
  window.addEventListener('load', scheduleNonCritical);
}

// Unregister Service Worker - was causing cache issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach(registration => registration.unregister());
  });
}

// Ensure root element exists before rendering
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('Root element not found. Make sure <div id="root"></div> exists in index.html');
}

// Yield to main thread so static LCP can paint, then render React (reduces TBT)
function renderApp() {
  try {
    createRoot(rootElement).render(
      <HelmetProvider>
        <App />
      </HelmetProvider>
    );
  } catch (error) {
    console.error('Failed to render React app:', error);
    rootElement.innerHTML = `
      <div style="padding: 40px; text-align: center; font-family: system-ui, sans-serif;">
        <h1 style="color: #dc2626; margin-bottom: 16px;">Erro ao carregar aplicação</h1>
        <p style="color: #6b7280; margin-bottom: 24px;">Ocorreu um erro ao carregar a aplicação. Por favor, recarregue a página.</p>
        <button onclick="location.reload()" style="background: #1e40af; color: white; border: none; padding: 12px 24px; border-radius: 6px; cursor: pointer; font-size: 16px;">
          Recarregar Página
        </button>
      </div>
    `;
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error);
    }
  }
}
requestAnimationFrame(() => { renderApp(); });
